"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type MarketListItem } from "@/lib/api";
import { usePickLocale } from "@/lib/usePickLocale";

/**
 * 2026-04-29 — Stocktwits-style market overview ticker.
 *
 * Stocktwits shows a horizontal scrolling row of index tickers (DIA /
 * SPY / QQQ + sentiment) at the top of /home. Granbay's prediction-
 * market analog: a thin scroller of the highest-volume active binary
 * markets with their current YES price. Each tile links to the market
 * detail page.
 *
 * Renders as an additive top-of-/home strip. Designed to feel like
 * the existing Granbay surface (uses our color tokens, our typography,
 * compact horizontally-scrollable rail) — only the mechanic is from
 * Stocktwits, the visual stays Granbay.
 *
 * Hidden silently when the API errors or returns nothing — the home
 * page still works without the rail.
 */
export function MarketsTickerBar() {
  const { t } = useTranslation();
  const [items, setItems] = useState<MarketListItem[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .listMarkets({ status: "active", sort: "trending", limit: 8 })
      .then((res) => {
        if (cancelled) return;
        // Filter to binary so the YES price is meaningful. Multi-
        // outcome rows would render YES=null and look broken.
        setItems(res.markets.filter((m) => m.kind === "binary"));
      })
      .catch(() => {
        // Decorative — fail silently.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!items || items.length === 0) return null;

  return (
    <div
      className="mb-4 -mx-1 overflow-x-auto"
      role="region"
      aria-label={t("marketsTicker.ariaLabel")}
    >
      <div className="flex gap-2 px-1 pb-1">
        {items.map((m) => (
          <TickerTile key={m.id} m={m} />
        ))}
      </div>
    </div>
  );
}

function TickerTile({ m }: { m: MarketListItem }) {
  const pick = usePickLocale();
  const title = typeof m.title === "string" ? m.title : pick(m.title);
  const yes = m.yesPriceCents;
  // Pricer color: > 50 = bullish lean, < 50 = bearish, == 50 muted.
  // Mirrors Stocktwits's red/green ticker treatment but on Granbay's
  // yes/no tokens so it stays consistent with the rest of the app.
  const tone =
    yes === null || yes === 50
      ? "var(--color-text2)"
      : yes > 50
        ? "var(--color-yes)"
        : "var(--color-no)";
  return (
    <Link
      href={`/markets/${m.slug}`}
      className="flex shrink-0 flex-col gap-0.5 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 transition-colors hover:border-[var(--color-brand)]"
      style={{ minWidth: 180, maxWidth: 240 }}
    >
      <p className="line-clamp-1 text-[11px] font-bold text-[var(--color-text)]">
        {title}
      </p>
      <div className="flex items-baseline gap-2">
        <span
          className="font-mono text-[14px] font-bold tabular-nums"
          style={{ color: tone }}
        >
          {yes !== null ? `${yes}¢` : "—"}
        </span>
        <span className="text-[9px] uppercase tracking-[0.4px] text-[var(--color-text3)]">
          YES
        </span>
        <span className="ml-auto text-[9px] uppercase tracking-[0.4px] text-[var(--color-text3)]">
          {m.categorySlug}
        </span>
      </div>
    </Link>
  );
}
